import { ErrorOutline, WarningAmber } from '@mui/icons-material';
import { Alert, Box, Paper, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../../intl/messages';
import { MOBILE_LOG_SEVERITY, WARNING_TEXT_COLOR, formatDurationMs } from '../../utils/mobileCameraLog';
import { getEvtMessage } from './logEvents';

const Tile = ({ label, value, color, icon, sub, subColor, onClick }) => (
	<Paper
		variant="outlined"
		onClick={onClick}
		tabIndex={onClick ? 0 : undefined}
		role={onClick ? 'button' : undefined}
		onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick(e) : undefined}
		sx={{ p: 1, minWidth: 96, textAlign: 'center', cursor: onClick ? 'pointer' : 'default' }}
	>
		<Typography sx={{ fontSize: 22, fontWeight: 700, color: color || 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
			{icon}
			{value}
		</Typography>
		<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
		{sub &&
			(Array.isArray(sub) ? sub : [sub]).map((line, i) => (
				<Typography key={i} sx={{ fontSize: 11, color: subColor || 'text.secondary' }}>
					{line}
				</Typography>
			))}
	</Paper>
);

const LogSummary = ({ summary, verdict, milestones, truncated, onSeverityFilterClick, onSeekMilestone }) => {
	const intl = useIntl();

	const verdictParts = [];
	verdictParts.push(
		verdict.hasRecording
			? intl.formatMessage(messages.mobileLogVerdictRecorded, {
					recorded: formatDurationMs(intl, verdict.recordedMs),
					total: formatDurationMs(intl, verdict.totalMs),
				})
			: intl.formatMessage(messages.mobileLogVerdictNoRecording)
	);

	if (verdict.severity === 'success') {
		verdictParts.push(intl.formatMessage(messages.mobileLogVerdictOk));
	} else {
		const problems = [];
		if (verdict.disconnects > 0) problems.push(intl.formatMessage(messages.mobileLogVerdictDisconnects, { count: verdict.disconnects }));
		if (verdict.offlineMs > 0) {
			problems.push(intl.formatMessage(messages.mobileLogVerdictOfflineMinutes, { minutes: Math.max(1, Math.round(verdict.offlineMs / 60000)) }));
		}
		if (verdict.uploadFails > 0) problems.push(intl.formatMessage(messages.mobileLogVerdictUploadFails, { count: verdict.uploadFails }));
		if (verdict.crashCount > 0) problems.push(intl.formatMessage(messages.mobileLogVerdictCrash, { count: verdict.crashCount }));
		if (problems.length) verdictParts.push(problems.join(', ') + '.');

		if (verdict.endedOffline) verdictParts.push(intl.formatMessage(messages.mobileLogVerdictEndedOffline));
		else if (verdict.endedDisconnected) verdictParts.push(intl.formatMessage(messages.mobileLogVerdictAbruptEnd));
	}

	if (verdict.endsBeforeExamMinutes) {
		verdictParts.push(intl.formatMessage(messages.mobileLogVerdictEndsBeforeExam, { minutes: verdict.endsBeforeExamMinutes }));
	}

	// lastHeartbeat only holds momentary state (socket/online/queueDepth) - its upload counters reset on
	// every mobile-side flush, so the tile needs the backend's session-wide aggregate instead. null means
	// the log has no heartbeat with data at all (not just zero uploads).
	const uploadTotals = summary?.uploadTotals;
	const uploaded = uploadTotals?.chunksUploaded;
	const produced = uploadTotals?.chunksProduced;
	const uploadsFailed = uploadTotals?.uploadsFailed ?? 0;
	const chunksLost = uploadTotals?.chunksLost ?? 0;

	const uploadSubLines = [];
	if (uploadsFailed > 0) uploadSubLines.push(`${intl.formatMessage(messages.mobileLogUploadsFailed)}: ${uploadsFailed}`);
	if (chunksLost > 0) uploadSubLines.push(`${intl.formatMessage(messages.mobileLogChunksLost)}: ${chunksLost}`);

	return (
		<Box>
			<Alert severity={verdict.severity} variant="outlined" sx={{ fontSize: 15, fontWeight: 600, mb: 2 }}>
				{verdictParts.join(' ')}
			</Alert>
			<Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					<Tile label={intl.formatMessage(messages.mobileLogDuration)} value={formatDurationMs(intl, summary?.durationMs)} />
					<Tile
						label={intl.formatMessage(messages.mobileLogErrors)}
						value={summary?.severityCounts?.error ?? 0}
						color={summary?.severityCounts?.error > 0 ? 'error.main' : 'text.disabled'}
						icon={summary?.severityCounts?.error > 0 ? <ErrorOutline fontSize="small" /> : null}
						onClick={() => onSeverityFilterClick(MOBILE_LOG_SEVERITY.ERROR)}
					/>
					<Tile
						label={intl.formatMessage(messages.mobileLogWarnings)}
						value={summary?.severityCounts?.warning ?? 0}
						color={summary?.severityCounts?.warning > 0 ? WARNING_TEXT_COLOR : 'text.disabled'}
						icon={summary?.severityCounts?.warning > 0 ? <WarningAmber fontSize="small" /> : null}
						onClick={() => onSeverityFilterClick(MOBILE_LOG_SEVERITY.WARNING)}
					/>
					<Tile
						label={intl.formatMessage(messages.mobileLogDisconnects)}
						value={summary?.disconnects ?? 0}
						color={summary?.disconnects > 0 ? 'error.main' : 'text.disabled'}
					/>
					<Tile
						label={intl.formatMessage(messages.mobileLogOffline)}
						value={verdict.offlineMs > 0 ? formatDurationMs(intl, verdict.offlineMs) : 0}
						color={verdict.offlineMs > 0 ? 'error.main' : 'text.disabled'}
					/>
					<Tile
						label={intl.formatMessage(messages.mobileLogUploads)}
						value={uploadTotals != null ? `${uploaded ?? '?'}/${produced ?? '?'}` : '-'}
						color={uploadsFailed > 0 || chunksLost > 0 ? 'error.main' : 'success.main'}
						sub={uploadSubLines.length ? uploadSubLines : null}
						subColor="error.main"
					/>
				</Box>
				{milestones?.length > 0 && (
					<Box sx={{ minWidth: 220, flex: 1 }}>
						<Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
							<FormattedMessage {...messages.mobileLogKeyMoments} />
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
							{milestones.map((entry, index) => {
								const msg = getEvtMessage(entry.evt);
								const isFlag = entry.evt === 'mobile.duplicate_disconnected' || entry.severity !== MOBILE_LOG_SEVERITY.INFO;
								return (
									<Typography
										key={`${entry.t}-${entry.evt}-${index}`}
										component="button"
										onClick={() => onSeekMilestone(entry)}
										sx={{
											fontSize: 12,
											textAlign: 'left',
											background: 'none',
											border: 'none',
											p: 0,
											cursor: 'pointer',
											color: isFlag ? 'error.main' : 'text.primary',
											'&:hover': { textDecoration: 'underline' },
										}}
									>
										{intl.formatTime(entry.t, { hour: '2-digit', minute: '2-digit' })}{' '}
										{msg ? intl.formatMessage(msg) : intl.formatMessage(messages.mobileLogEvtUnknown)}
									</Typography>
								);
							})}
						</Box>
					</Box>
				)}
			</Box>
			{/* bigger visual weight than the parse note below - it casts doubt on every number above it,
			the counts/timeline only cover the (truncated) part of the log the backend kept */}
			{truncated && (
				<Alert severity="warning" sx={{ mt: 1 }}>
					<FormattedMessage {...messages.mobileLogTruncated} />
				</Alert>
			)}
			{(summary?.duplicatesRemoved > 0 || summary?.malformedLines > 0) && (
				<Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
					<FormattedMessage
						{...messages.mobileLogParseNote}
						values={{ duplicates: summary.duplicatesRemoved, malformed: summary.malformedLines }}
					/>
				</Typography>
			)}
		</Box>
	);
};

export default LogSummary;
